<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '../stores/user'
import {
  LayoutDashboard,
  Home,
  Users,
  UserCheck,
  UserCog,
  User,
  Building2,
  Building,
  Store,
  ShoppingBag,
  CreditCard,
  Wallet,
  WalletCards,
  ArrowLeftRight,
  ShieldAlert,
  ShieldCheck,
  Shield,
  Smartphone,
  Scale,
  FileText,
  ChevronRight,
  LogOut,
  Settings,
  Sliders,
  Key,
  KeyRound,
  Menu as MenuIcon,
  FolderTree,
  ListTree,
  Tags,
  BookOpen,
  Receipt,
  ShoppingCart,
  Package,
  Boxes,
  Ticket,
  BadgePercent,
  Coins,
  History,
  Activity,
  BarChart3,
  Lock,
  Cpu,
  Monitor,
  Zap,
  Radio,
  Network,
  Terminal,
  Layers,
  ArrowUpRight
} from 'lucide-vue-next'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

// 图标映射
const iconMap: Record<string, any> = {
  Dashboard: LayoutDashboard,
  Home: Home,
  LayoutDashboard: LayoutDashboard,
  Activity: Activity,
  BarChart: BarChart3,

  // 用户与权限
  Users: Users,
  User: User,
  UserCog: UserCog,
  UserCheck: UserCheck,
  Role: ShieldCheck,
  Shield: Shield,
  ShieldCheck: ShieldCheck,
  Key: Key,
  KeyRound: KeyRound,
  Permission: Lock,

  // 组织与主体
  Building2: Building2,
  Building: Building,
  Tenant: Building2,
  Merchant: Store,
  Store: Store,
  ShoppingBag: ShoppingBag,

  // 支付与网关核心
  CreditCard: CreditCard,
  Wallet: Wallet,
  WalletCards: WalletCards,
  ArrowLeftRight: ArrowLeftRight,
  Receipt: Receipt,
  Coins: Coins,
  Transaction: ArrowLeftRight,
  Withdrawal: WalletCards,
  Channel: CreditCard,

  // 设备与监控
  Smartphone: Smartphone,
  Scale: Scale,
  Cpu: Cpu,
  Device: Smartphone,
  Monitor: Monitor,
  Postcard: Network,
  Document: Terminal,
  History: History,

  // 系统配置
  Settings: Settings,
  Setting: Settings,
  Sliders: Sliders,
  Dict: BookOpen,
  Menu: MenuIcon,
  FolderTree: FolderTree,
  ListTree: ListTree,
  Tags: Tags,
  ShieldAlert: ShieldAlert
}

// 智能获取本地组件图标
const getIcon = (iconName?: string) => {
  if (!iconName) return Layers
  if (iconMap[iconName]) return iconMap[iconName]
  
  const normalized = iconName.trim().toLowerCase()
  for (const [key, iconComp] of Object.entries(iconMap)) {
    if (key.toLowerCase() === normalized) {
      return iconComp
    }
  }

  if (normalized.includes('order') || normalized.includes('gateway')) return Receipt
  if (normalized.includes('monitor') || normalized.includes('task')) return Network
  if (normalized.includes('channel') || normalized.includes('log')) return Terminal
  if (normalized.includes('open') || normalized.includes('app') || normalized.includes('key')) return Key

  return Layers
}

// 智能匹配二级子菜单组件图标
const getSubmenuIcon = (child: MenuItem) => {
  if (child.icon && iconMap[child.icon]) {
    return iconMap[child.icon]
  }

  const name = child.name || ''
  if (name.includes('订单')) return Receipt
  if (name.includes('任务') || name.includes('推送') || name.includes('通知')) return Network
  if (name.includes('日志') || name.includes('通道') || name.includes('交互')) return Terminal
  if (name.includes('应用') || name.includes('密钥') || name.includes('凭据')) return Key
  if (name.includes('监控')) return Activity
  if (name.includes('设置') || name.includes('配置')) return Settings

  if (child.icon) return getIcon(child.icon)

  return Radio
}

// 角色名称映射
const roleNameMap: Record<string, string> = {
  'gateway_admin': '网关管理员',
  'super_admin': '系统管理员',
  'admin': '平台管理员'
}

const getRoleName = (role?: string): string => {
  if (!role) return '网关管理员'
  return roleNameMap[role] || role
}

// 菜单项类型
interface MenuItem {
  id: string
  name: string
  path: string
  icon?: string
  children?: MenuItem[]
}

// 展开的菜单ID集合
const expandedMenus = ref<Set<string>>(new Set())

// 从 store 获取菜单列表
const menuList = computed<MenuItem[]>(() => {
  return userStore.menus.map(menu => ({
    id: menu.id,
    name: menu.name,
    path: menu.path,
    icon: menu.icon,
    children: menu.children?.map(child => ({
      id: child.id,
      name: child.name,
      path: child.path,
      icon: child.icon
    }))
  }))
})

// 检查子路由路径是否精确/前缀匹配
const isChildActive = (childPath: string) => {
  if (!childPath) return false
  return route.path === childPath || route.path.startsWith(childPath + '/')
}

// 检查父菜单项下是否有激活的子页面
const isParentActive = (menu: MenuItem) => {
  if (route.path === menu.path) return true
  if (menu.children && menu.children.length > 0) {
    return menu.children.some(child => isChildActive(child.path))
  }
  return false
}

// 检查单节点菜单是否完全匹配当前路由
const isSelfActive = (path: string) => {
  return route.path === path
}

// 检查菜单当前是否处于展开状态
const isExpanded = (menu: MenuItem) => {
  return expandedMenus.value.has(menu.id)
}

// 自动向 Set 添加包含当前路由的父菜单 id
const autoExpandActiveParent = () => {
  menuList.value.forEach(menu => {
    if (menu.children && menu.children.length > 0) {
      if (menu.children.some(child => isChildActive(child.path))) {
        const nextSet = new Set(expandedMenus.value)
        nextSet.add(menu.id)
        expandedMenus.value = nextSet
      }
    }
  })
}

watch(
  [() => route.path, () => menuList.value],
  () => {
    autoExpandActiveParent()
  },
  { immediate: true, deep: true }
)

const toggleMenu = (menu: MenuItem) => {
  if (menu.children && menu.children.length > 0) {
    const nextSet = new Set(expandedMenus.value)
    if (nextSet.has(menu.id)) {
      nextSet.delete(menu.id)
    } else {
      nextSet.add(menu.id)
    }
    expandedMenus.value = nextSet
  }
}

const handleNavigate = (path: string) => {
  router.push(path)
}

const handleMenuClick = (menu: MenuItem) => {
  if (menu.children && menu.children.length > 0) {
    toggleMenu(menu)
  } else {
    router.push(menu.path)
  }
}

const handleLogout = () => {
  userStore.logout()
}
</script>

<template>
  <aside class="w-64 h-screen bg-slate-900 flex flex-col fixed left-0 top-0 z-50 border-r border-slate-800 shadow-xl select-none font-sans">
    <!-- Logo 标头区 -->
    <div class="h-16 flex items-center px-5 border-b border-slate-800 shrink-0">
      <div class="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center mr-3 shadow-sm shrink-0">
        <Zap class="w-4.5 h-4.5 text-white" />
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1.5">
          <span class="text-base font-bold text-white tracking-wide">JDY</span>
          <span class="text-[11px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400 font-mono font-medium">GATEWAY</span>
        </div>
        <p class="text-[11px] text-slate-400 font-mono mt-0.5">支付网关管理控制台</p>
      </div>
    </div>

    <!-- 菜单区域 -->
    <nav class="flex-1 overflow-y-auto py-4 px-3 custom-scrollbar">
      <ul class="space-y-1.5">
        <li v-for="menu in menuList" :key="menu.id">
          <!-- 一级菜单项 (text-[15px]) -->
          <button
            @click="handleMenuClick(menu)"
            class="w-full flex items-center px-3.5 py-2.5 rounded-xl text-[15px] font-medium transition-all duration-200 group relative select-none border"
            :class="[
              isParentActive(menu) || (!(menu.children && menu.children.length > 0) && isSelfActive(menu.path))
                ? 'bg-cyan-500/15 text-cyan-400 font-semibold border-cyan-500/20 shadow-sm'
                : 'text-slate-300 hover:bg-slate-800/60 hover:text-white border-transparent'
            ]"
          >
            <!-- 激活高亮边条 -->
            <div
              v-if="isParentActive(menu) || (!(menu.children && menu.children.length > 0) && isSelfActive(menu.path))"
              class="absolute left-0 top-2 bottom-2 w-1.5 bg-cyan-500 rounded-r shadow-[0_0_8px_rgba(6,182,212,0.5)]"
            ></div>

            <!-- 图标容器 -->
            <div
              class="w-7 h-7 rounded-lg flex items-center justify-center mr-3 shrink-0 transition-colors duration-200"
              :class="[
                isParentActive(menu) || (!(menu.children && menu.children.length > 0) && isSelfActive(menu.path))
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'bg-slate-800/80 text-slate-400 group-hover:bg-slate-700/80 group-hover:text-slate-200'
              ]"
            >
              <component :is="getIcon(menu.icon)" class="w-4 h-4" />
            </div>

            <span class="flex-1 text-left truncate tracking-wide">{{ menu.name }}</span>

            <ChevronRight
              v-if="menu.children && menu.children.length > 0"
              class="w-4 h-4 transition-transform duration-200 ease-in-out shrink-0 ml-1"
              :class="[
                isExpanded(menu) ? 'rotate-90' : '',
                isParentActive(menu) ? 'text-cyan-400' : 'text-slate-500 group-hover:text-slate-300'
              ]"
            />
          </button>

          <!-- 二级子菜单 (text-sm) -->
          <transition name="submenu">
            <ul
              v-if="menu.children && menu.children.length > 0 && isExpanded(menu)"
              class="mt-1.5 ml-4 pl-3.5 border-l-2 border-slate-800 space-y-1"
            >
              <li v-for="child in menu.children" :key="child.id">
                <button
                  @click="handleNavigate(child.path)"
                  class="w-full flex items-center px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group select-none"
                  :class="isChildActive(child.path)
                    ? 'bg-cyan-500/15 text-cyan-400 font-semibold shadow-sm'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'"
                >
                  <component
                    :is="getSubmenuIcon(child)"
                    class="w-4 h-4 mr-2.5 shrink-0 transition-colors duration-200"
                    :class="isChildActive(child.path)
                      ? 'text-cyan-400'
                      : 'text-slate-500 group-hover:text-slate-300'"
                  />
                  <span class="truncate tracking-wide">{{ child.name }}</span>
                </button>
              </li>
            </ul>
          </transition>
        </li>
      </ul>
    </nav>

    <!-- 底部运维人员信息卡 -->
    <div class="p-3.5 border-t border-slate-800 shrink-0 bg-slate-900/80">
      <div class="flex items-center mb-2.5 px-1">
        <div class="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-cyan-400 font-semibold text-xs shrink-0">
          {{ (userStore.userInfo?.nickname?.charAt(0) || userStore.userInfo?.username?.charAt(0) || 'G').toUpperCase() }}
        </div>
        <div class="ml-2.5 flex-1 min-w-0">
          <p class="text-xs font-medium text-slate-200 truncate">
            {{ userStore.userInfo?.nickname || userStore.userInfo?.username || 'gwadmin' }}
          </p>
          <p class="text-[11px] text-slate-500 truncate mt-0.5">
            {{ getRoleName(userStore.userInfo?.roles?.[0]) }}
          </p>
        </div>
      </div>
      <button
        @click="handleLogout"
        class="w-full flex items-center justify-center px-3 py-1.5 rounded text-xs font-medium text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-colors"
      >
        <LogOut class="w-3.5 h-3.5 mr-1.5" />
        退出登录
      </button>
    </div>
  </aside>
</template>

<style scoped>
/* 滚动条定制 */
.custom-scrollbar::-webkit-scrollbar {
  width: 3px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #1e293b;
  border-radius: 2px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #334155;
}

/* 子菜单展开折叠动画 */
.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 500px;
  opacity: 1;
  overflow: hidden;
}

.submenu-enter-from,
.submenu-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}
</style>
